/**
 * TasksView — F 83 Stage 2 unified task surface.
 *
 * Renders one of the 5 sidebar task sections (INBOX / TODAY / UPCOMING /
 * ANYTIME / DONE) with row-level processing actions:
 *   - Click checkbox: mark done / restore
 *   - Per-row action menu: move to another view status, set due date, delete
 *
 * Reads tasks via TaskStore. Subscribes to task:event so the view stays
 * in sync with QuickCaptureModal and TaskBlock writes.
 *
 * Detail pane + drag-reorder are scoped for a later dispatch. This view
 * focuses on the 5-status navigation surface that F 82 Stage 2 verdict
 * identified as the structural missing piece.
 */

import './TasksView.css';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import type { Task, TaskViewStatus } from '../../../types/models/Task';
import { TASK_VIEW_STATUSES, normalizeTaskStatus } from '../../../types/models/Task';
import { TaskStore } from '../../../core/task/store';
import { TaskDetailPane } from './TaskDetailPane';

export interface TasksViewProps {
  /** The view to render. */
  status: TaskViewStatus;
  store?: TaskStore;
}

const VIEW_META: Record<TaskViewStatus, { icon: string; label: string; empty: string }> = {
  inbox:    { icon: '📥', label: 'Inbox',    empty: 'No new tasks. Press Cmd+T to capture one.' },
  today:    { icon: '📅', label: 'Today',    empty: 'Nothing scheduled for today. Enjoy the headspace.' },
  upcoming: { icon: '🛫', label: 'Upcoming', empty: 'No upcoming tasks. Plan ahead from Anytime.' },
  anytime:  { icon: '🌿', label: 'Anytime',  empty: 'No anytime tasks. Move things here when they have no fixed date.' },
  done:     { icon: '✓',  label: 'Done',     empty: 'Nothing completed yet. Tick a task to mark it done.' },
};

/** Returns true if a task belongs in the given view. */
function belongsIn(task: Task, view: TaskViewStatus, todayDate: string): boolean {
  const normalized = normalizeTaskStatus(task.status);
  if (view === 'done') {
    // Done view shows terminal tasks (normalized === 'done', covers both
    // 'done' and legacy 'cancelled')
    return normalized === 'done';
  }
  if (normalized !== view) return false;
  if (view === 'today') {
    // If user-set status='today', include. If dueDate=today and status is
    // 'upcoming'/'anytime', still belongs here. The view filter already
    // restricts to normalized === 'today', so 'upcoming'/'anytime' due
    // today won't show — that's intentional for v1.
    return true;
  }
  return true;
}

interface ActionMenuState {
  taskId: string;
  anchor: { top: number; left: number };
}

export const TasksView: React.FC<TasksViewProps> = ({ status, store }) => {
  const internalStore = useMemo(() => store ?? new TaskStore(), [store]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [menu, setMenu] = useState<ActionMenuState | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  /** Drag state (refs — drag listeners attach to window, not the item). */
  const draggingIdRef = useRef<string | null>(null);

  /** Drag handlers — pointer-event based, no heavy DnD lib. */
  const onDragHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>, taskId: string) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const item = handle.closest('.fc-tasks-view__item') as HTMLElement | null;
      if (!item) return;
      const startY = e.clientY;
      const rect = item.getBoundingClientRect();
      const offsetY = startY - rect.top;
      draggingIdRef.current = taskId;
      const list = item.parentElement!;
      item.setPointerCapture?.(e.pointerId);

      const findOverId = (clientY: number): string | null => {
        const rows = Array.from(list.querySelectorAll('.fc-tasks-view__item')) as HTMLElement[];
        const over = rows.find((r) => {
          if (r === item) return false;
          const r2 = r.getBoundingClientRect();
          return clientY >= r2.top && clientY <= r2.bottom;
        });
        return over?.dataset.taskId ?? null;
      };

      const move = (ev: PointerEvent) => {
        const y = ev.clientY - offsetY;
        item.style.position = 'absolute';
        item.style.zIndex = '5';
        item.style.width = `${rect.width}px`;
        item.style.left = `${rect.left}px`;
        item.style.top = `${y}px`;
        setDragOverId(findOverId(ev.clientY));
      };
      const up = async (ev: PointerEvent) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        item.style.position = '';
        item.style.zIndex = '';
        item.style.width = '';
        item.style.left = '';
        item.style.top = '';
        const targetId = draggingIdRef.current;
        const overId = findOverId(ev.clientY);
        setDragOverId(null);
        draggingIdRef.current = null;
        if (!targetId || !overId || targetId === overId) return;
        // Read current visible ids from DOM at drop time (always freshest).
        const rows = Array.from(list.querySelectorAll('.fc-tasks-view__item')) as HTMLElement[];
        const visibleIds = rows.map((r) => r.dataset.taskId!).filter(Boolean);
        const targetIdx = visibleIds.indexOf(overId);
        if (targetIdx < 0) return;
        await internalStore.reorder(targetId, targetIdx, status);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [internalStore, status],
  );

  // Load + subscribe
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const all = await internalStore.list();
      if (!alive) return;
      setTasks(all);
      setLoaded(true);
    };
    void refresh();
    const unsub = internalStore.subscribe(() => { void refresh(); });
    return () => { alive = false; unsub(); };
  }, [internalStore]);

  // Close action menu on outside click / Escape
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('.fc-tasks-view__action-menu') && !t.closest('.fc-tasks-view__action-trigger')) {
        setMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const today = new Date();
  const todayDate = today.toISOString().slice(0, 10);
  const meta = VIEW_META[status];

  const items = useMemo(() => {
    return tasks
      .filter((t) => belongsIn(t, status, todayDate))
      .sort((a, b) => {
        // Order field first, then priority, then createdAt
        const orderDelta = (a.order ?? 0) - (b.order ?? 0);
        if (orderDelta !== 0) return orderDelta;
        const priorityDelta = a.priority - b.priority;
        if (priorityDelta !== 0) return priorityDelta;
        return a.createdAt.localeCompare(b.createdAt);
      });
  }, [tasks, status, todayDate]);

  const onToggleDone = useCallback(async (task: Task) => {
    if (normalizeTaskStatus(task.status) === 'done') {
      await internalStore.reopen(task.id);
    } else {
      await internalStore.complete(task.id);
    }
  }, [internalStore]);

  const onMove = useCallback(async (task: Task, target: TaskViewStatus) => {
    if (normalizeTaskStatus(task.status) === target) {
      setMenu(null);
      return;
    }
    const next: Task = {
      ...task,
      status: target,
      completedAt: target === 'done' ? task.completedAt ?? new Date().toISOString() : null,
      updatedAt: new Date().toISOString(),
    };
    await internalStore.save(next);
    setMenu(null);
  }, [internalStore]);

  const onDelete = useCallback(async (task: Task) => {
    await internalStore.delete(task.id);
    setMenu(null);
  }, [internalStore]);

  const onSetDue = useCallback(async (task: Task, value: string) => {
    const next: Task = {
      ...task,
      due: value === '' ? null : value,
      updatedAt: new Date().toISOString(),
    };
    await internalStore.save(next);
    setMenu(null);
  }, [internalStore]);

  return (
    <div className="fc-tasks-view" data-testid="fc-tasks-view" data-view={status}>
      <header className="fc-tasks-view__header">
        <h2>
          <span aria-hidden="true">{meta.icon}</span> {meta.label}
          <span className="fc-tasks-view__count" data-testid="fc-tasks-view-count">
            {' '}({items.length})
          </span>
        </h2>
        {/* F 201 A — the view had NO affordance to create a task. Measured on
            production: each of the five views rendered exactly one button in
            the main area (the header avatar), so the only entry points were
            the sidebar's "New task" and the Cmd+T shortcut named in the
            empty-state copy.
            This dispatches the SAME `fc:quick-capture:toggle` event that
            PageTree's "New task" button dispatches, so there is one source of
            truth for opening the capture (A.2) rather than a second path. */}
        <button
          type="button"
          className="fc-tasks-view__new"
          data-testid="fc-tasks-view-new"
          onClick={() => window.dispatchEvent(new CustomEvent('fc:quick-capture:toggle'))}
        >
          <span aria-hidden="true">+</span> New task
        </button>
      </header>
      {!loaded && <p className="fc-tasks-view__loading">Loading…</p>}
      {loaded && items.length === 0 && (
        <p className="fc-tasks-view__empty" data-testid="fc-tasks-view-empty">{meta.empty}</p>
      )}
      {loaded && items.length > 0 && (
        <ul className="fc-tasks-view__list" data-testid="fc-tasks-view-list">
          {items.map((t) => (
            <li
              key={t.id}
              className={
                'fc-tasks-view__item' +
                (normalizeTaskStatus(t.status) === 'done' ? ' fc-tasks-view__item--done' : '') +
                (dragOverId === t.id ? ' fc-tasks-view__item--drag-over' : '')
              }
              data-task-id={t.id}
              data-task-status={t.status}
              data-testid="fc-tasks-view-item"
            >
              <span
                className="fc-tasks-view__drag-handle"
                aria-label="Drag to reorder"
                role="button"
                tabIndex={0}
                data-testid="fc-tasks-view-drag-handle"
                onPointerDown={(e) => onDragHandlePointerDown(e, t.id)}
              >
                ⋮⋮
              </span>
              <button
                type="button"
                className="fc-tasks-view__checkbox"
                aria-pressed={normalizeTaskStatus(t.status) === 'done'}
                aria-label={
                  normalizeTaskStatus(t.status) === 'done'
                    ? 'Mark task open'
                    : 'Mark task done'
                }
                data-testid="fc-tasks-view-checkbox"
                onClick={() => void onToggleDone(t)}
              >
                {normalizeTaskStatus(t.status) === 'done' ? '☑' : '☐'}
              </button>
              <span
                className="fc-tasks-view__title"
                role="button"
                tabIndex={0}
                data-testid="fc-tasks-view-title"
                onClick={() => setDetailTaskId(t.id)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setDetailTaskId(t.id)}
              >
                {t.title}
              </span>
              {t.due && (
                <span
                  className={
                    'fc-tasks-view__due' +
                    (t.due < todayDate && normalizeTaskStatus(t.status) !== 'done'
                      ? ' fc-tasks-view__due--overdue'
                      : '')
                  }
                >
                  {t.due}
                </span>
              )}
              <span className="fc-tasks-view__priority" data-priority={t.priority}>
                P{t.priority}
              </span>
              <button
                type="button"
                className="fc-tasks-view__action-trigger"
                aria-label="Task actions"
                data-testid="fc-tasks-view-actions"
                onClick={(e) => {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setMenu({
                    taskId: t.id,
                    anchor: { top: rect.bottom + 4, left: rect.right - 200 },
                  });
                }}
              >
                ⋯
              </button>
            </li>
          ))}
        </ul>
      )}

      {menu && (
        <ActionMenu
          menu={menu}
          tasks={tasks}
          onMove={onMove}
          onDelete={onDelete}
          onSetDue={onSetDue}
          onClose={() => setMenu(null)}
        />
      )}
      {detailTaskId && (() => {
        const dt = tasks.find((t) => t.id === detailTaskId);
        return dt ? (
          <TaskDetailPane
            task={dt}
            store={internalStore}
            onClose={() => setDetailTaskId(null)}
          />
        ) : null;
      })()}
    </div>
  );
};

interface ActionMenuProps {
  menu: ActionMenuState;
  tasks: Task[];
  onMove: (task: Task, target: TaskViewStatus) => Promise<void>;
  onDelete: (task: Task) => Promise<void>;
  onSetDue: (task: Task, value: string) => Promise<void>;
  onClose: () => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ menu, tasks, onMove, onDelete, onSetDue, onClose }) => {
  const task = tasks.find((t) => t.id === menu.taskId);
  if (!task) return null;
  const dueInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      className="fc-tasks-view__action-menu"
      data-testid="fc-tasks-view-action-menu"
      style={{ top: menu.anchor.top, left: menu.anchor.left }}
      role="menu"
    >
      <div className="fc-tasks-view__action-menu-section">
        <span className="fc-tasks-view__action-menu-heading">Move to</span>
        {TASK_VIEW_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={
              'fc-tasks-view__action-menu-item' +
              (normalizeTaskStatus(task.status) === s
                ? ' fc-tasks-view__action-menu-item--active'
                : '')
            }
            data-testid={`fc-tasks-view-move-${s}`}
            onClick={() => void onMove(task, s)}
            role="menuitem"
          >
            {VIEW_META[s].icon} {VIEW_META[s].label}
          </button>
        ))}
      </div>
      <div className="fc-tasks-view__action-menu-section">
        <span className="fc-tasks-view__action-menu-heading">Due date</span>
        <div className="fc-tasks-view__action-menu-row">
          <input
            ref={dueInputRef}
            type="date"
            className="fc-tasks-view__action-menu-date"
            defaultValue={task.due ?? ''}
            data-testid="fc-tasks-view-set-due"
          />
          <button
            type="button"
            className="fc-tasks-view__action-menu-item"
            onClick={() => void onSetDue(task, dueInputRef.current?.value ?? '')}
          >
            Set
          </button>
        </div>
      </div>
      <div className="fc-tasks-view__action-menu-section">
        <button
          type="button"
          className="fc-tasks-view__action-menu-item fc-tasks-view__action-menu-item--danger"
          data-testid="fc-tasks-view-delete"
          onClick={() => void onDelete(task)}
          role="menuitem"
        >
          Delete task
        </button>
      </div>
    </div>
  );
};

export default TasksView;
